import _ from 'lodash';
import { createSelector } from 'reselect';

import { getSelectedProjectId } from '../../redux/projects/projects.selectors';
import { getSelectedOrganization } from '../../selectors/organizations/organizations';
import { getMemberInfo } from '../../selectors/session/session';
import { Organization } from '../../types/entities';
import {
  isMemberOrganizationAdmin,
  isMemberOrganizationOwner,
  isMemberOrganizationViewer,
  isMemberProjectAdmin,
  isMemberProjectViewer,
} from '../team/team-member-modal/team-member-form/team-member-form.utils';
import { TeamMemberSecurityGroup } from '../team/team.types';
import { normalizeSecurityGroups } from '../team/team.utils';

import { Role, RolesPermissions } from './role.types';

const getUserRole = (
  memberSecurityGroups: TeamMemberSecurityGroup[],
  isCurrentMemberOrganizationOwner: boolean,
  projectId: string | undefined,
): Role | undefined => {
  if (
    isCurrentMemberOrganizationOwner ||
    isMemberOrganizationOwner(memberSecurityGroups)
  ) {
    return Role.OrganizationOwner;
  }

  if (isMemberOrganizationAdmin(memberSecurityGroups)) {
    return Role.OrganizationAdmin;
  }

  if (projectId && isMemberProjectAdmin(memberSecurityGroups, projectId)) {
    return Role.ProjectAdmin;
  }

  if (isMemberOrganizationViewer(memberSecurityGroups)) {
    return Role.OrganizationViewer;
  }

  if (projectId && isMemberProjectViewer(memberSecurityGroups, projectId)) {
    return Role.ProjectViewer;
  }
};

const getUserRoleWithoutProject = (
  memberSecurityGroups: TeamMemberSecurityGroup[],
  isCurrentMemberOrganizationOwner: boolean,
): Role | undefined => {
  if (
    isCurrentMemberOrganizationOwner ||
    isMemberOrganizationOwner(memberSecurityGroups)
  ) {
    return Role.OrganizationOwner;
  }

  if (isMemberOrganizationAdmin(memberSecurityGroups)) {
    return Role.OrganizationAdmin;
  }

  if (isMemberProjectAdmin(memberSecurityGroups)) {
    return Role.ProjectAdmin;
  }

  if (isMemberOrganizationViewer(memberSecurityGroups)) {
    return Role.OrganizationViewer;
  }

  if (isMemberProjectViewer(memberSecurityGroups)) {
    return Role.ProjectViewer;
  }
};

export const getCurrentUserRole = createSelector(
  getMemberInfo,
  getSelectedOrganization,
  getSelectedProjectId,
  (
    memberInfo,
    organization: Organization,
    projectId: string,
  ): Role | undefined => {
    if (!memberInfo) {
      return;
    }

    const memberSecurityGroups = normalizeSecurityGroups(
      memberInfo.memberships[organization.id].securityGroups,
    );
    const isCurrentMemberOrganizationOwner =
      organization.ownerId === memberInfo.id;

    return getUserRole(
      memberSecurityGroups,
      isCurrentMemberOrganizationOwner,
      projectId,
    );
  },
);

export const getCurrentUserRoleWithoutProject = createSelector(
  getMemberInfo,
  getSelectedOrganization,
  (memberInfo, organization: Organization): Role | undefined => {
    if (!memberInfo) {
      return;
    }

    const memberSecurityGroups = normalizeSecurityGroups(
      memberInfo.memberships[organization.id].securityGroups,
    );
    const isCurrentMemberOrganizationOwner =
      organization.ownerId === memberInfo.id;

    return getUserRoleWithoutProject(
      memberSecurityGroups,
      isCurrentMemberOrganizationOwner,
    );
  },
);

export const hasCurrentUserPermissions = createSelector(
  getCurrentUserRole,
  (currentUserRole): ((rolesPermissions: RolesPermissions) => boolean) =>
    _.memoize((rolesPermissions: RolesPermissions): boolean => {
      if (!currentUserRole) {
        return false;
      }

      return rolesPermissions[currentUserRole];
    }),
);
